import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import Input from "./Input";
import InputWrap from "./InputWrap";
import SelectField from "./SelectField";
import { classNamesPrefixed } from "../../../helpers/CoreHelpers";

const LINK_TYPE = Object.freeze({
  INTERNAL: 1,
  EXTERNAL: 2,
});

const DEFAULT_VALUES = Object.freeze({
  type: LINK_TYPE.INTERNAL,
  title: "",
  newTab: false,
  url: "",
  contentId: null,
});

const NewLinkField = (props) => {
  const { content, workspace, input } = props;
  const {
    value: { type, title, newTab, url, contentId },
    name,
  } = input;

  // TODO Add support for nested optgroups in react-select
  const getPageGroup = (page) => {
    const { id, title, contentDefinitionKey, children } = page;

    const label = `${title} (${contentDefinitionKey})`;
    const hasChildren = children.length > 0;

    if (hasChildren) {
      return {
        label,
        options: children.map((childPage) => getPageGroup(childPage)),
      };
    }

    return {
      value: id,
      label,
    };
  };

  const getRecursiveChildren = (page) => {
    const { id, title, contentDefinitionKey, children } = page;
    const subOptions = children
      .map((childPage) => getRecursiveChildren(childPage))
      .reduce((acc, curr) => {
        if (Array.isArray(curr)) {
          return [...acc, ...curr];
        }
        return [curr, ...acc];
      }, []);
    return [
      {
        id,
        title,
        contentDefinitionKey,
      },
      ...subOptions,
    ];
  };

  const pagesOptions = useMemo(() => {
    return workspace.items.map((ws) => {
      const subOptions = content.items
        .filter((o) => o.workspaceId === ws.id)
        .map((c) => getRecursiveChildren(c))
        .reduce((acc, curr) => {
          if (Array.isArray(curr)) {
            return [...acc, ...curr];
          }
          return [curr, ...acc];
        }, [])
        .sort((a, b) => {
          if (a.contentDefinitionKey > b.contentDefinitionKey) {
            return 1;
          } else if (a.contentDefinitionKey < b.contentDefinitionKey) {
            return -1;
          }

          if (a.title > b.title) {
            return 1;
          } else if (a.title < b.title) {
            return -1;
          } else {
            return 0;
          }
        })
        .map((o) => ({
          id: o.id,
          title: `${o.title} (${o.contentDefinitionKey})`,
        }));

      return {
        label: ws.title,
        options: subOptions,
      };
    });
  }, [content, workspace]);

  const defaultPageOption =
    content.flat
      .map((c) => ({
        id: c.id,
        title: `${c.title} (${c.contentDefinitionKey})`,
      }))
      .find((o) => o.id === contentId) || null;

  useEffect(() => {
    if (typeof input.value === "string") {
      input.onChange({ ...DEFAULT_VALUES });
    }
  }, []);

  const handleOnChange = (e) => {
    const {
      target,
      target: { name, type },
    } = e;
    let value = type === "checkbox" ? target.checked : target.value;

    input.onChange({
      ...input.value,
      [name.includes("type") ? "type" : name]: name.includes("type")
        ? parseInt(value, 10)
        : value,
    });
  };

  const handleSelectChange = (id) => {
    input.onChange({
      ...input.value,
      contentId: id,
    });
  };

  const renderLinkType = () => {
    switch (type) {
      case 2:
        return (
          <Input
            type="text"
            name="url"
            label="External link"
            placeholder="Type url"
            value={url}
            onChange={handleOnChange}
          />
        );
      case 1:
      default:
        return (
          <div className="csod-awave--show-language-picker">
            <SelectField
              label="Internal link"
              placeholder="Search by title"
              items={pagesOptions}
              omitCollapsible={true}
              defaultValue={defaultPageOption}
              input={{ value: contentId }}
              afterOnChange={handleSelectChange}
            />
          </div>
        );
    }
  };

  return (
    <div className={classNamesPrefixed({ "new-link-field": true })}>
      <div style={{ marginBottom: "20px" }}>
        <InputWrap label="Link type">
          <label style={{ marginRight: "10px" }}>
            <input
              type="radio"
              name={`${name}_type`}
              value={LINK_TYPE.INTERNAL}
              checked={type === 1}
              onChange={handleOnChange}
            />{" "}
            Internal
          </label>
          <label>
            <input
              type="radio"
              name={`${name}_type`}
              value={LINK_TYPE.EXTERNAL}
              checked={type === 2}
              onChange={handleOnChange}
            />{" "}
            External
          </label>
        </InputWrap>
      </div>
      <div style={{ marginBottom: "20px" }}>
        <Input
          type="text"
          name="title"
          label="Link title"
          placeholder="Type title"
          value={title}
          onChange={handleOnChange}
        />
      </div>
      <div style={{ marginBottom: "12px" }}>{renderLinkType()}</div>
      <div>
        <label className="check">
          <input
            type="checkbox"
            name="newTab"
            checked={newTab}
            onChange={handleOnChange}
          />{" "}
          <span>Open link in new tab</span>
        </label>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  workspace: state.workspace,
  content: state.content,
});

export default connect(
  mapStateToProps,
  undefined
)(NewLinkField);



// WEBPACK FOOTER //
// ./src/core/admin/components/Fields/NewLinkField.js